import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Student, Books, ClipboardText, ChartBar, Clock, CheckCircle,
  Warning, PaperPlaneTilt, TrendUp, User, Paperclip, X, DownloadSimple,
} from "@phosphor-icons/react";

const KIND_LABEL = {
  homework: "Devoir",
  quiz: "Interro.",
  project: "Projet",
  exam: "Examen",
  essay: "Rédaction",
  presentation: "Exposé",
};

const ACCEPT_MIME = "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,image/jpeg,image/png,image/webp,application/zip,application/x-zip-compressed";

export default function StudentDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSubmit, setOpenSubmit] = useState(null);        // assignment obj
  const [submissionText, setSubmissionText] = useState("");
  const [submissionFiles, setSubmissionFiles] = useState([]); // [{url, filename, size, mime}]
  const [handwritten, setHandwritten] = useState(false);      // devoir remis en manuscrit (papier)
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/student/dashboard");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openSubmitDialog = (a) => {
    setOpenSubmit(a);
    setSubmissionText(a.submission?.content || "");
    setSubmissionFiles(a.submission?.attachments || []);
    setHandwritten(!!a.submission?.submitted_handwritten);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const r = await api.post("/uploads/document", { data_url: b64, filename: file.name, kind: "submission" });
      setSubmissionFiles(a => [...a, { url: r.data.url, filename: r.data.filename, size: r.data.size, mime: r.data.mime }]);
      toast.success(`Fichier joint : ${file.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };
  const onSubmissionFiles = async (e) => {
    for (const f of e.target.files || []) await uploadFile(f);
    e.target.value = "";
  };

  const submit = async () => {
    if (!openSubmit) return;
    if (!submissionText && submissionFiles.length === 0 && !handwritten) {
      toast.error("Ajoutez au moins un fichier, du texte, ou cochez 'remis en manuscrit'");
      return;
    }
    setSaving(true);
    try {
      await api.post("/submissions", {
        assignment_id: openSubmit.id, content: submissionText,
        attachments: submissionFiles, submitted_handwritten: handwritten,
      });
      toast.success("Remise envoyée");
      setOpenSubmit(null); setSubmissionText(""); setSubmissionFiles([]); setHandwritten(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-sm text-muted-foreground">Chargement…</div>;
  if (!data) return null;

  const { student, current_class, counts, courses, assignments, recent_grades, active_period } = data;

  const classLabel = current_class
    ? [current_class.name, current_class.stream || current_class.series].filter(Boolean).join(" · ")
    : null;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Ma scolarité</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
            <Student size={32} weight="duotone" className="text-primary" />
            {student.first_name} {student.last_name}
          </h1>
          <div className="mt-1 text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Matricule <b className="font-mono">{student.matricule}</b></span>
            {classLabel && (
              <>
                <span>·</span>
                <span data-testid="student-class-label">Classe <b className="text-primary">{classLabel}</b></span>
              </>
            )}
            {active_period && <><span>·</span><span>{active_period.name} en cours</span></>}
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Books} label="Cours suivis" value={counts.courses} testid="stat-student-courses" tint="primary" />
        <StatCard icon={ClipboardText} label="Devoirs à rendre" value={counts.upcoming_assignments} testid="stat-student-upcoming" tint={counts.upcoming_assignments > 0 ? "warning" : "muted"} />
        <StatCard icon={CheckCircle} label="Devoirs notés" value={counts.graded_count} testid="stat-student-graded" tint="secondary" />
        <StatCard icon={TrendUp} label="Moyenne récente" value={counts.avg_score ?? "—"} testid="stat-student-avg" tint="accent" />
      </div>

      <Tabs defaultValue="assignments">
        <TabsList data-testid="tabs-student">
          <TabsTrigger value="assignments" data-testid="tab-student-assignments">Devoirs ({assignments.upcoming.length + assignments.submitted.length + assignments.graded.length})</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-student-courses">Mes cours ({courses.length})</TabsTrigger>
          <TabsTrigger value="grades" data-testid="tab-student-grades">Mes notes ({recent_grades.length})</TabsTrigger>
        </TabsList>

        {/* ---------- ASSIGNMENTS ---------- */}
        <TabsContent value="assignments" className="mt-6 space-y-6">
          <AssignmentSection title="À rendre" empty="Aucun devoir en attente. 🎉" tint="accent" list={assignments.upcoming}
            onSubmit={(a) => { setOpenSubmit(a); setSubmissionText(""); }} />
          <AssignmentSection title="Rendus (en attente de correction)" empty="—" tint="primary" list={assignments.submitted}
            onSubmit={(a) => { setOpenSubmit(a); setSubmissionText(a.submission?.content || ""); }} />
          <AssignmentSection title="Corrigés" empty="—" tint="secondary" list={assignments.graded} onSubmit={null} />
        </TabsContent>

        {/* ---------- COURSES ---------- */}
        <TabsContent value="courses" className="mt-6">
          {courses.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground text-sm">
              Vous n'êtes inscrit(e) à aucun cours pour cette année académique.
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map(c => (
                <Card key={c.course_id} className="p-5 card-hover" data-testid={`card-student-course-${c.course_id}`}>
                  <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center mb-3">
                    <Books size={20} weight="duotone" />
                  </div>
                  <div className="font-heading font-semibold">{c.subject_name}
                    {c.subject_code && <span className="text-xs text-muted-foreground ml-2 font-mono">{c.subject_code}</span>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">Coef. {c.coefficient}</div>
                  {c.teacher_names.length > 0 && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs">
                      <User size={12} /> {c.teacher_names.join(", ")}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---------- GRADES ---------- */}
        <TabsContent value="grades" className="mt-6">
          {recent_grades.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground text-sm">
              Aucune note publiée pour l'instant.
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3">Matière</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Période</th>
                    <th className="px-4 py-3 text-right">Note</th>
                    <th className="px-4 py-3">Commentaire</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_grades.map((g, i) => (
                    <tr key={g.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/20" : ""}`} data-testid={`row-grade-${g.id}`}>
                      <td className="px-4 py-3 font-medium">{g.subject_name}</td>
                      <td className="px-4 py-3 text-xs capitalize">{g.assessment_type}</td>
                      <td className="px-4 py-3 text-xs">{g.period}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-mono font-semibold">
                        {g.score}<span className="text-muted-foreground">/{g.max_score}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs truncate">{g.comment || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Submit dialog */}
      <Dialog open={!!openSubmit} onOpenChange={(v) => { if (!v) { setOpenSubmit(null); setSubmissionText(""); setSubmissionFiles([]); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Remettre : {openSubmit?.title}</DialogTitle>
            <DialogDescription>
              {openSubmit?.subject_name} · {KIND_LABEL[openSubmit?.kind] || "Devoir"}
              {openSubmit?.due_date ? ` · à rendre avant ${new Date(openSubmit.due_date).toLocaleString("fr-FR")}` : ""}
            </DialogDescription>
          </DialogHeader>
          {openSubmit?.description && (
            <div className="text-sm bg-muted/50 rounded p-3 whitespace-pre-wrap max-h-32 overflow-y-auto">
              {openSubmit.description}
            </div>
          )}
          {openSubmit?.attachments?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-muted-foreground mb-1">Consignes / ressources du professeur</div>
              <div className="space-y-1">
                {openSubmit.attachments.map((a, i) => (
                  <a key={i} href={a.url?.startsWith("http") ? a.url : `${process.env.REACT_APP_BACKEND_URL}${a.url}`}
                     target="_blank" rel="noreferrer"
                     className="flex items-center gap-2 text-xs p-2 bg-muted rounded hover:bg-muted/70">
                    <DownloadSimple size={12} /> {a.filename}
                  </a>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Ma réponse (facultatif si je joins des fichiers)</div>
            <Textarea rows={4} placeholder="Description de mon travail, notes, corrections…"
                      value={submissionText} onChange={e => setSubmissionText(e.target.value)}
                      data-testid="input-submission" />
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground mb-1">
              Mes fichiers (PDF, Word, Excel, PowerPoint, image, ZIP — 20 Mo/fichier · joignez jusqu'à 2 fichiers pour votre devoir/examen)
            </div>
            <div className="space-y-2">
              {submissionFiles.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-muted rounded text-xs" data-testid={`sub-file-${i}`}>
                  <span className="flex items-center gap-2 min-w-0">
                    <Paperclip size={12} />
                    <span className="truncate">{a.filename}</span>
                    {a.size ? <span className="text-muted-foreground">({Math.round(a.size / 1024)} Ko)</span> : null}
                  </span>
                  <button type="button" onClick={() => setSubmissionFiles(l => l.filter((_, j) => j !== i))}
                          className="shrink-0" data-testid={`sub-file-remove-${i}`}>
                    <X size={14} className="text-destructive" />
                  </button>
                </div>
              ))}
              <input type="file" multiple accept={ACCEPT_MIME} onChange={onSubmissionFiles} disabled={uploading}
                     className="text-xs" data-testid="input-submission-files" />
              {uploading && <div className="text-xs text-muted-foreground">Téléversement en cours…</div>}
              {submissionFiles.length > 0 && submissionFiles.length < 2 && ["exam","project"].includes(openSubmit?.kind) && (
                <div className="text-[11px] text-muted-foreground">Vous pouvez joindre un second fichier (jusqu'à 2 recommandés).</div>
              )}
            </div>
          </div>
          <label className="flex items-start gap-2 p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer" data-testid="handwritten-label">
            <input type="checkbox" checked={handwritten}
                   onChange={e => setHandwritten(e.target.checked)}
                   className="h-4 w-4 rounded border-input mt-0.5"
                   data-testid="checkbox-handwritten" />
            <span className="text-sm">
              <b>Devoir remis en manuscrit</b>
              <span className="block text-[11px] text-muted-foreground">Cochez si vous avez rendu ce travail sur papier au professeur. Vous pouvez malgré tout joindre une photo/scan.</span>
            </span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpenSubmit(null); setSubmissionText(""); setSubmissionFiles([]); setHandwritten(false); }}>Annuler</Button>
            <Button onClick={submit} disabled={uploading || saving}
                    className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-submit-work">
              <PaperPlaneTilt size={16} className="mr-2" /> Remettre {submissionFiles.length > 0 && `(${submissionFiles.length} fichier${submissionFiles.length > 1 ? "s" : ""})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignmentSection({ title, empty, tint, list, onSubmit }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-1.5 h-6 rounded-full bg-${tint === "accent" ? "accent" : tint === "secondary" ? "secondary" : "primary"}`} />
        <h3 className="font-heading font-semibold">{title} <span className="text-muted-foreground text-sm font-normal">({list.length})</span></h3>
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-muted-foreground pl-3">{empty}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map(a => {
            const due = a.due_date ? new Date(a.due_date) : null;
            const overdue = due && new Date() > due && !a.submission;
            return (
              <Card key={a.id} className={`p-4 ${overdue ? "border-accent/50" : ""}`} data-testid={`card-assignment-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{a.subject_name} · {KIND_LABEL[a.kind] || "Devoir"}</div>
                    <div className="mt-1 font-heading font-semibold truncate">{a.title}</div>
                    {a.description && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{a.description}</div>}
                  </div>
                  <Badge variant="outline" className="shrink-0">/{a.max_score}</Badge>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className={`flex items-center gap-1 ${overdue ? "text-accent font-semibold" : "text-muted-foreground"}`}>
                    {overdue ? <Warning size={13} weight="fill" /> : <Clock size={13} />}
                    {due ? due.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "Sans date limite"}
                  </span>
                  {a.submission?.status === "graded" && (
                    <span className="font-mono font-semibold text-secondary tabular-nums" data-testid={`grade-${a.id}`}>
                      {a.submission.grade}/{a.max_score}
                    </span>
                  )}
                  {a.submission?.status === "submitted" && <Badge variant="outline" className="text-[10px]">Rendu</Badge>}
                  {a.submission?.status === "late" && <Badge variant="outline" className="text-[10px] border-accent text-accent">Rendu en retard</Badge>}
                </div>
                {a.submission?.feedback && (
                  <div className="mt-2 text-[11px] bg-muted/50 rounded p-2 border-l-2 border-secondary">
                    <span className="font-semibold">Retour :</span> {a.submission.feedback}
                  </div>
                )}
                {onSubmit && (
                  <Button size="sm" variant="outline" className="w-full mt-3" onClick={() => onSubmit(a)}
                          data-testid={`btn-open-submit-${a.id}`}>
                    {a.submission ? "Modifier ma remise" : "Remettre le travail"} <PaperPlaneTilt size={14} className="ml-1" />
                  </Button>
                )}
                {a.submission?.attachments?.length > 0 && (
                  <div className="mt-2 text-[10px] text-muted-foreground">
                    {a.submission.attachments.length} fichier(s) joint(s)
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, tint }) {
  const styles = {
    primary: "bg-primary/10 text-primary",
    secondary: "bg-secondary/10 text-secondary",
    accent: "bg-accent/10 text-accent",
    warning: "bg-accent/15 text-accent",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-md ${styles[tint] || styles.muted} flex items-center justify-center mb-3`}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className="font-heading text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wide">{label}</div>
    </Card>
  );
}
